import { useState } from 'react';

import { useStoreActions, useStoreState } from '~/store/hooks';
import useFeature from '~/store/clinic/hooks/useFeature';
import {
  clientMapper,
  ITeamMemberMapped,
  sortByName,
  teamMemberMapper,
} from '~/utils/clientAllocation';
import { useApiRequest } from '~/hooks/api';
import api from '~/services/api';
import AllocationTypes from '~/ui/pages/ActTeam/reusable/ClientAllocationForm/constants/allocationTypes';
import { IOption } from '~/types';

import { IUseGetEditClientAllocationsParams, IUseGetEditClientAllocationsResult } from '../types';

const useGetEditClientAllocationsForTeamAdmin = ({
  actTeamId,
  clientAllocationDate,
  enabled = true,
}: IUseGetEditClientAllocationsParams): IUseGetEditClientAllocationsResult => {
  const [teamMembers, setTeamMembers] = useState<ITeamMemberMapped[]>([]);
  const [clients, setClients] = useState<IOption[]>([]);

  const { clinic: userClinic } = useStoreState(state => state.user.current);
  const clientAllocations = useStoreState(state => state.clientAllocation.current) || [];

  const onGetMyClinic = useStoreActions(actions => actions.clinic.onGetMyClinic);
  const { onGetClinicActTeam } = useStoreActions(actions => actions.actTeam);
  const { onGetClientAllocation, onGetAllocationClientsByGroup } = useStoreActions(
    actions => actions.clientAllocation,
  );
  const { isCrisisAvailable } = useFeature();

  const { isLoading, refetch } = useApiRequest(
    async () => {
      const clinicId = String(userClinic.id);
      const allocationDate = `${clientAllocationDate}T00:00:00Z`;

      // Step 1: Fetch clinic and team data
      await onGetMyClinic();
      await onGetClinicActTeam({ clinicId, actTeamId });

      // Step 2: Fetch available clients and team members
      const [clientsResponse, teamMembersResponse] = await Promise.all([
        api.clientAllocation.getRelatedClients(clinicId, actTeamId),
        api.actTeamMember.getActTeamMemberList(clinicId, actTeamId),
      ]);

      setClients(clientsResponse.data.map(clientMapper));
      setTeamMembers(teamMembersResponse.data.sort(sortByName).map(teamMemberMapper));

      // Step 3: Fetch client allocations
      await onGetClientAllocation({
        clinicId,
        teamId: actTeamId,
        clientAllocationDate: allocationDate,
        allocationType: isCrisisAvailable ? AllocationTypes.ALL : AllocationTypes.STANDARD,
      });

      await onGetAllocationClientsByGroup({ clinicId, teamId: actTeamId });
    },
    {
      enabled,
      dependencies: [actTeamId, clientAllocationDate, userClinic.id],
    },
  );

  return {
    isLoading,
    teamMembers,
    clients,
    allocations: clientAllocations,
    refetch,
  };
};

export default useGetEditClientAllocationsForTeamAdmin;
